import PropTypes from "prop-types";
import { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import SFTextField from ".";
import getStyled from "../../hooks/getStyled";
import { SFSearchIcon, SFCloseIcon } from "../icons";
import InputAdornment from "@mui/material/InputAdornment";
import SFIconButton from "../button/iconButton";

const { styled } = getStyled();

const StyledContainer = styled("div")(({ theme }) => ({
  display: "flex",
  marginRight: theme.spacing(1),
}));

const SFSearchFieldStyled = styled(SFTextField)(({ theme, variant }) => ({
  "& .MuiInputBase-root": {
    paddingLeft: theme.spacing(1),
    ...(variant === "filled" && {
      "&:before, &:after": {
        borderBottom: "none !important",
      },
      "& .MuiInputBase-input": {
        paddingTop: theme.spacing(1.5),
        paddingBottom: theme.spacing(1.5),
      },
    }),
  },
}));

const SFSearchField = forwardRef(({
  onChange,
  placeholder,
  className,
  variant = "outlined",
  size = "medium",
  hasClearBtn = false,
  initialValue = "",
}, ref) => {
  const [value, setValue] = useState(initialValue ?? "");
  const [firstRender, setFirstRender] = useState(true);

  useImperativeHandle(ref, () => ({
    api: {
      clear: () => setValue(""),
    }
  }));

  const handleChange = (e) => {
    setValue(e.target.value);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!firstRender && (value.length > 2 || value.length === 0)) {
        onChange(value);
      } else {
        setFirstRender(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [value]);

  return (
    <SFSearchFieldStyled
      variant={variant}
      size={size}
      placeholder={placeholder}
      value={value}
      onChange={handleChange}
      className={className}
      InputProps={{
        startAdornment: (
          <StyledContainer>
            <SFSearchIcon />
          </StyledContainer>
        ),
        ...(hasClearBtn &&
          value.length > 0 && {
          endAdornment: (
            <InputAdornment position="end">
              <SFIconButton onClick={() => setValue("")}>
                <SFCloseIcon size="small" />
              </SFIconButton>
            </InputAdornment>
          ),
        }),
      }}
    />
  );
});

SFSearchField.propTypes = {
  // Called when there is a useful change in the search value
  onChange: PropTypes.func,
  // Placeholder when search value is empty
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  // Needed in order to use the getStyled hook
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
};

export default SFSearchField;
